import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const isMixpanelAvailable = !!MIXPANEL_TOKEN;

/** Set to true only after initMixpanel() has run; prevents track before init. */
let mixpanelInitialized = false;

/** Context merged into every event (user_id, project_id, organisation_id, environment) */
let eventContext = {};

export const initMixpanel = () => {
  return new Promise((resolve) => {
    if (!isMixpanelAvailable) {
      console.warn("Mixpanel token not available. Analytics will be disabled.");
      resolve(false);
      return;
    }

    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.REACT_APP_ENVIRONMENT === "staging",
      track_pageview: false,
      ignore_dnt: true,
      persistence: "cookie",
      cross_subdomain_cookie: true,
      cross_site_cookie: true,
      secure_cookie: true,
      record_sessions_percent: 100,
      record_heatmap_data: true,
      record_canvas: false,
      record_idle_timeout_ms: 30 * 60 * 1000,
      record_block_class: /^(no-capture|sensitive|private)$/,
      record_block_selector:
        "[data-no-capture], [data-sensitive], [data-private]",
      record_mask_text_selector:
        'input[type="password"], input[type="email"], .mask-text, [data-mask-text]',
      record_max_ms: 2 * 60 * 60 * 1000,
      record_min_ms: 5000,
    });
    mixpanelInitialized = true;
    resolve(true);
  });
};

/**
 * Set context included on every subsequent trackEvent (user_id, project_id, organisation_id, environment).
 * Call from App when user/project/organisation changes.
 */
export const setEventContext = (context = {}) => {
  eventContext = { ...context };
};

/** Only track when token exists and init has been called (avoids track-before-init error). */
const shouldTrack = () =>
  isMixpanelAvailable && mixpanelInitialized;

export const trackEvent = (eventName, props = {}) => {
  if (!shouldTrack()) return;
  try {
    const merged = { ...eventContext, ...props };
    mixpanel.track(eventName, merged);
  } catch (error) {
    console.warn("Failed to track Mixpanel event:", error);
  }
};

export const identifyUser = (userId, traits = {}) => {
  if (!isMixpanelAvailable) return;

  try {
    if (!userId) {
      // Reset Mixpanel for anonymous session
      mixpanel.reset();
      return;
    }

    // Identify the user
    mixpanel.identify(userId);

    // Set people properties if any exist
    if (Object.keys(traits).length > 0) {
      mixpanel.people.set({
        ...traits,
        $last_login: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.warn("Failed to identify user in Mixpanel:", error);
  }
};

/**
 * Start session recording manually (ignores sampling rate)
 * Useful for recording specific user flows or sessions
 */
export const startSessionRecording = () => {
  if (!isMixpanelAvailable) return;
  try {
    mixpanel.start_session_recording();
  } catch (error) {
    console.warn("Failed to start Mixpanel session recording:", error);
  }
};

/**
 * Stop session recording manually
 * Useful for stopping recording on sensitive pages or after specific events
 */
export const stopSessionRecording = () => {
  if (!isMixpanelAvailable) return;
  try {
    mixpanel.stop_session_recording();
  } catch (error) {
    console.warn("Failed to stop Mixpanel session recording:", error);
  }
};

/**
 * Get session recording properties (e.g., $mp_replay_id)
 * Useful for linking custom events to a specific replay
 * @returns {Object} Recording properties or empty object if not recording
 */
export const getSessionRecordingProperties = () => {
  if (!isMixpanelAvailable) return {};
  try {
    return mixpanel.get_session_recording_properties();
  } catch (error) {
    console.warn("Failed to get Mixpanel session recording properties:", error);
    return {};
  }
};

/**
 * Get URL to view the current replay in Mixpanel UI
 * @returns {string|null} Replay URL or null if no replay is active
 */
export const getSessionReplayUrl = () => {
  if (!isMixpanelAvailable) return null;
  try {
    return mixpanel.get_session_replay_url();
  } catch (error) {
    console.warn("Failed to get Mixpanel session replay URL:", error);
    return null;
  }
};
